import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import { fivemLogAPI, authAPI } from '../services/api';
import {
    FiHome, FiFileText, FiAlertTriangle, FiDollarSign, FiLogOut,
    FiMenu, FiX, FiWifi, FiWifiOff, FiDatabase
} from 'react-icons/fi';
import '../styles/Layout.css';

const FIVEM_LOG_TABLES = [
    { key: 'rvn_money_logs',     label: 'Giao Dịch Tiền', icon: '💰', color: '#34d399' },
    { key: 'rvn_item_logs',      label: 'Vật Phẩm',        icon: '📦', color: '#a78bfa' },
    { key: 'rvn_vehicle_logs',   label: 'Phương Tiện',     icon: '🚗', color: '#60a5fa' },
    { key: 'rvn_anticheat_logs', label: 'Chống Hack',      icon: '🛡️', color: '#fbbf24' },
    { key: 'rvn_explosion_logs', label: 'Nổ Bom',          icon: '💥', color: '#fb923c' },
    { key: 'rvn_action_logs',    label: 'Hành Động',       icon: '🎬', color: '#9ca3af' },
    { key: 'rvn_admin_logs',     label: 'Admin',            icon: '🔧', color: '#e879f9' },
    { key: 'rvn_damage_logs',    label: 'Sát Thương',       icon: '⚔️', color: '#f87171' },
    { key: 'rvn_death_logs',     label: 'Tử Vong',          icon: '💀', color: '#ef4444' },
    { key: 'rvn_private_logs',   label: 'Log Private',      icon: '🔒', color: '#fbbf24' },
    { key: 'rvn_big_transfer_logs', label: 'Chuyển Tiền Lớn',     icon: '💸', color: '#f97316' },
    { key: 'rvn_rapid_gain_logs',   label: 'Tăng Tiền Bất Thường', icon: '⚡', color: '#ef4444' },
    { key: 'rvn_admin_spam_logs',   label: 'Admin Spam Lệnh',      icon: '🛡️', color: '#dc2626' },
    { key: 'rvn_admin_favor_logs',  label: 'Thiên Vị Người Chơi',  icon: '🎯', color: '#dc2626' },
    { key: 'rvn_admin_self_logs',   label: 'Admin Tự Buff',        icon: '🪞', color: '#dc2626' },
    { key: 'rvn_modshift_logs',     label: 'Lịch Trực Mod',        icon: '📋', color: '#10b981' },
];

const Layout = () => {
    const { user, logout } = useAuth();
    const { connected, newLogCount, clearNewLogCount } = useWebSocket();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [tableCounts, setTableCounts] = useState({});
    const [role, setRole] = useState('admin');

    const location = useLocation();

    useEffect(() => {
        authAPI.me().then(r => setRole(r.data.role || 'admin')).catch(() => {});
    }, []);

    // Modview role guard — auto-redirect khỏi route không cho phép
    useEffect(() => {
        if (role !== 'modview') return;
        const path = location.pathname;
        const MODVIEW_OK = (
            path.startsWith('/group/rvn_modshift_logs') ||
            path.startsWith('/fivem-player/')
        );
        if (!MODVIEW_OK) navigate('/group/rvn_modshift_logs', { replace: true });
    }, [role, location.pathname, navigate]);

    useEffect(() => {
        fivemLogAPI.getSummary()
            .then(res => {
                const map = {};
                (res.data.tables || []).forEach(t => { map[t._id] = t.count; });
                setTableCounts(map);
            })
            .catch(() => {});
    }, []);

    // Tab nào modview được phép thấy
    const isModview = role === 'modview';
    const visibleTables = isModview
        ? FIVEM_LOG_TABLES.filter(t => t.key === 'rvn_modshift_logs')
        : FIVEM_LOG_TABLES;

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const fmtCount = (n) => {
        if (!n) return null;
        return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
    };

    return (
        <div className={`layout ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
            {/* Sidebar */}
            <aside className="sidebar">
                <div className="sidebar-header">
                    <div className="logo">
                        <span className="logo-icon">🏙️</span>
                        {sidebarOpen && <span className="logo-text">RVN City</span>}
                    </div>
                    <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
                        {sidebarOpen ? <FiX /> : <FiMenu />}
                    </button>
                </div>

                {/* WS Status */}
                <div className="ws-status">
                    {connected
                        ? <><FiWifi className="ws-icon connected" />{sidebarOpen && <span>Realtime ON</span>}</>
                        : <><FiWifiOff className="ws-icon disconnected" />{sidebarOpen && <span>Connecting...</span>}</>
                    }
                </div>

                <nav className="sidebar-nav">
                    {/* Main nav items */}
                    {!isModview && (
                        <NavLink to="/" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} end>
                            <span className="nav-icon"><FiHome /></span>
                            {sidebarOpen && <span className="nav-label">Dashboard</span>}
                            {sidebarOpen && newLogCount > 0 && (
                                <span className="nav-badge" onClick={(e) => { e.preventDefault(); clearNewLogCount(); }}>
                                    {newLogCount > 99 ? '99+' : newLogCount}
                                </span>
                            )}
                        </NavLink>
                    )}

                    {!isModview && <>
                        <NavLink to="/logs" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                            <span className="nav-icon"><FiFileText /></span>
                            {sidebarOpen && <span className="nav-label">Tất Cả Logs</span>}
                        </NavLink>

                        <NavLink to="/statistics" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                            <span className="nav-icon"><FiAlertTriangle /></span>
                            {sidebarOpen && <span className="nav-label">Log Người Chơi</span>}
                        </NavLink>

                        <NavLink to="/economy" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                            <span className="nav-icon"><FiDollarSign /></span>
                            {sidebarOpen && <span className="nav-label">Kinh Tế</span>}
                        </NavLink>

                        <NavLink to="/players" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                            <span className="nav-icon">👥</span>
                            {sidebarOpen && <span className="nav-label">Danh Sách Người Chơi</span>}
                        </NavLink>

                        <NavLink to="/alerts" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                            <span className="nav-icon">🚨</span>
                            {sidebarOpen && <span className="nav-label">Cảnh Báo Exploit</span>}
                        </NavLink>

                        <NavLink to="/job-abuse" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                            <span className="nav-icon">🧨</span>
                            {sidebarOpen && <span className="nav-label">Anti-Hack Job</span>}
                        </NavLink>
                    </>}

                    {/* FiveM Log Tables */}
                    {sidebarOpen && (
                        <div className="nav-section-title">
                            <FiDatabase className="nav-section-icon" />
                            {isModview ? 'KHU VỰC TRỰC' : 'LOG FIVEM REALTIME'}
                        </div>
                    )}

                    {visibleTables.map(tbl => (
                        <NavLink
                            key={tbl.key}
                            to={`/group/${tbl.key}`}
                            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                            title={!sidebarOpen ? tbl.label : ''}
                        >
                            <span className="nav-icon" style={{ fontSize: '1.1rem' }}>{tbl.icon}</span>
                            {sidebarOpen && (
                                <>
                                    <span className="nav-label">{tbl.label}</span>
                                    {tableCounts[tbl.key] != null && (
                                        <span className="nav-count">{fmtCount(tableCounts[tbl.key])}</span>
                                    )}
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>

                <div className="sidebar-footer">
                    <div className="user-info">
                        <img
                            src={user?.avatar || `https://cdn.discordapp.com/embed/avatars/0.png`}
                            alt={user?.username}
                            className="user-avatar"
                        />
                        {sidebarOpen && (
                            <div className="user-details">
                                <span className="user-name">{user?.username}</span>
                                <span className="user-role">Admin</span>
                            </div>
                        )}
                    </div>
                    <button className="logout-btn" onClick={handleLogout}>
                        <FiLogOut />
                        {sidebarOpen && <span>Logout</span>}
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="main-content">
                <Outlet />
            </main>
        </div>
    );
};

export default Layout;
